import {checkAuth, getDashboardConnectors} from "@/shared/api";
import {useAuthStore} from "@/store/auth";
import {useConnectorsStore} from "@/store/connectors";
import {DataConnectorsType} from "@/types/DataConnectorsType";
import {getAuthToken} from "@/utils/auth";
import {Layout, Row, Spin, ConfigProvider, theme as antdTheme} from "antd";
import {FC, useEffect, useState, useCallback, useMemo, useRef} from "react";
import {Outlet, useNavigate, useLocation, Navigate} from "react-router-dom";
import {useTernaryDarkMode} from "usehooks-ts";
import AsideMenu from "@/components/aside-menu";
import { SubaccountBar } from "@/components/subaccount/SubaccountBar";
import AIAssistantTrigger from "@/components/ai-assistant/AIAssistantTrigger";
import NotificationsButton from "@/components/notifications-button";
import ProfileButton from "@/components/profile-button";
import { useMarketingCapture } from "@/marketingCapture/useMarketingCapture";
import MarketingCaptureGate from "@/marketingCapture/MarketingCaptureGate";
import { getStaffToken } from "@/adminHost";
import { useMaestroContext } from "@/contexts/MaestroContext";
import "./DashboardLayout.less";
// Loaded with the shell, not the lazy page, so the studio split is correct
// on the first client navigation (emotion `sx` is not).
import "@/pages/DataDimensionStudio/styles.scss";

const DashboardLayout: FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { loggedIn, user, initializeUser, logout } = useAuthStore();
	const connectors = useConnectorsStore(state => state.connectors);
	const setConnectors = useConnectorsStore(state => state.setConnectors);
	const { isDarkMode, ternaryDarkMode, setTernaryDarkMode } = useTernaryDarkMode({ localStorageKey: "theme" });
	const { allowed: captureMode, needsGate } = useMarketingCapture();
	const { pageData } = useMaestroContext();
	const themeBeforeCapture = useRef<typeof ternaryDarkMode | null>(null);
	const [loading, setLoading] = useState(true);
	const [collapsed, setCollapsed] = useState(false);

	// Determine AI Assistant context based on current page
	const getAIContext = useCallback((): 'integration-builder' | 'dashboard' | 'general' => {
		const path = location.pathname;
		if (path.includes('/studio')) return 'integration-builder';
		if (path.includes('/data-mapping')) return 'integration-builder';
		if (path.startsWith('/details/')) return 'integration-builder';
		if (path.startsWith('/integration')) return 'integration-builder';
		if (path.startsWith('/dashboard') || path === '/' || path.startsWith('/fleet')) return 'dashboard';
		return 'general';
	}, [location.pathname]);

	// Extract integration ID and name from URL and connectors store
	const integrationContext = useMemo(() => {
		const path = location.pathname;
		let integrationId: string | undefined;

		// Extract ID from various URL patterns
		if (path.startsWith('/details/')) {
			integrationId = path.split('/details/')[1]?.split('/')[0];
		} else if (path.includes('/data-mapping/')) {
			integrationId = path.split('/data-mapping/')[1]?.split('/')[0];
		} else if (path.includes('/integration/')) {
			integrationId = path.split('/integration/')[1]?.split('/')[0];
		}

		// Find connector in store to get the integration name
		const connector = integrationId ? connectors.find(c => c.connector?._id === integrationId) : undefined;
		const integrationName = connector?.connector?.integrationProfile?.integrationName 
			|| connector?.connector?.integrationName 
			|| connector?.connector?.name;

		return {
			integrationId,
			integrationName
		};
	}, [location.pathname, connectors]);

	// Memoize the reset function
	const resetUserState = useCallback(() => {
		logout();
		navigate('/sign-in');
	}, [logout, navigate]);

	// Memoize the update connectors function
	const updateConnectorsState = useCallback((connectors: DataConnectorsType[]) => {
		setConnectors(connectors || []);
	}, [setConnectors]);

	useEffect(() => {
		if (getStaffToken() && location.pathname.startsWith('/admin')) {
			setLoading(false);
			return;
		}

		const initializeData = async () => {
			const token = getAuthToken();

			if (!token) {
				setLoading(false);
				resetUserState();
				return;
			}

			try {
				// Only initialize user if not already initialized
				if (!user) {
					await initializeUser();
				}

				// Get connectors separately - if this fails, we still have user data
				try {
					const connectorsResponse = await getDashboardConnectors(token, true);
					updateConnectorsState(connectorsResponse);
				} catch (connectorError) {
					console.error("Error fetching connectors:", connectorError);
				}
			} catch (authError) {
				console.error("Error during authentication check:", authError);
				resetUserState();
			} finally {
				setLoading(false);
			}
		};

		initializeData();
	}, [resetUserState, initializeUser, updateConnectorsState, user, location.pathname, location.search, location.hash, navigate]);

	// Only redirect if we're done loading and not logged in
	useEffect(() => {
		if (getStaffToken() && location.pathname.startsWith('/admin')) return;
		if (!loading && loggedIn === false) {
			navigate('/sign-in');
		}
	}, [loggedIn, loading, location.pathname, navigate]);

	useEffect(() => {
		if (!captureMode) return;
		themeBeforeCapture.current = ternaryDarkMode;
		if (ternaryDarkMode !== "dark") {
			setTernaryDarkMode("dark");
		}
		return () => {
			const previous = themeBeforeCapture.current;
			themeBeforeCapture.current = null;
			if (previous && previous !== "dark") {
				setTernaryDarkMode(previous);
			}
		};
	}, [captureMode, setTernaryDarkMode]);

	const handleToggleCollapse = useCallback(() => {
		setCollapsed(prev => !prev);
	}, []);

	const staffChrome = Boolean(getStaffToken()) && location.pathname.startsWith('/admin');
	const embeddedMaestro = location.pathname.startsWith('/intelligence-insights');
	const showGlobalActions = loggedIn && !captureMode && !staffChrome && !loading && !needsGate;
	const globalActionsRef = useRef<HTMLDivElement>(null);

	// The cluster is position:fixed in the viewport corner. Measure it and publish
	// the reserved width so page headers and portaled drawers can stay clear of it.
	useEffect(() => {
		if (!showGlobalActions) {
			document.documentElement.style.removeProperty('--global-actions-clearance');
			return;
		}
		const el = globalActionsRef.current;
		if (!el) return;

		const gap = 20;
		const apply = () => {
			const rect = el.getBoundingClientRect();
			if (rect.width === 0) return;
			const clearance = Math.ceil(window.innerWidth - rect.left + gap);
			document.documentElement.style.setProperty('--global-actions-clearance', `${clearance}px`);
		};

		apply();
		const observer = new ResizeObserver(apply);
		observer.observe(el);
		window.addEventListener('resize', apply);
		return () => {
			observer.disconnect();
			window.removeEventListener('resize', apply);
		};
	}, [showGlobalActions]);

	if (loading) {
		return (
			<Row 
				justify="center" 
				align="middle" 
				style={{
					height: '100vh',
					width: '100%'
				}}
			>
				<Spin size="large"/>
			</Row>
		);
	}

	if (needsGate) {
		return <MarketingCaptureGate />;
	}

	if (location.pathname === "/data-mapping" && !captureMode) {
		return <Navigate to="/" replace />;
	}

	return (
		<ConfigProvider theme={{ algorithm: isDarkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm }}>
			<Layout className={`${isDarkMode ? "dark" : "light"} main-app-layout`}>
				{(loggedIn || staffChrome) && !captureMode && (
					<Layout.Sider 
						width={250} 
						className="main-sidebar"
						collapsible
						collapsed={collapsed}
						onCollapse={setCollapsed}
						breakpoint="lg"
						collapsedWidth={60}
						trigger={null}
					>
						<AsideMenu collapsed={collapsed} onToggleCollapse={handleToggleCollapse} />
					</Layout.Sider>
				)}
				<Layout.Content className="default-layout-content">
					{showGlobalActions && <SubaccountBar />}
					{/* Global action buttons - visible on all pages */}
					{showGlobalActions && (
						<div className="global-action-buttons" ref={globalActionsRef}>
							<NotificationsButton />
							<ProfileButton />
						</div>
					)}
					<Outlet/>
				</Layout.Content>
				
				{/* Maestro AI Assistant - Available on all pages when logged in */}
				{loggedIn && !captureMode && !staffChrome && !embeddedMaestro && (
					<AIAssistantTrigger 
						context={getAIContext()} 
						iconStyle="geometric"
						integrationId={integrationContext.integrationId}
						integrationName={integrationContext.integrationName}
						pageData={pageData || undefined}
					/>
				)}
			</Layout>
		</ConfigProvider>
	);
};

export default DashboardLayout;